// All marketing copy is HTML strings (will later be mapped to Firestore).

/* ===========================
   Hero Section
   =========================== */

   export const HERO_HEADLINE_HTML = `See your money clearly.<br/><span style="color:var(--pp-secondary)">Pocket Penny gives you control.</span>`;

   export const HERO_SUBTITLE_HTML = `Less stress. More control. A personal finance app built for real life—organized, private, and ready to grow with you.`;
   
   export const HERO_CTA_HTML = `
     <a href="#demo" class="btn btn-primary heroCTAButtons">Join the Public Beta</a>
     <a href="#features" class="btn btn-outline-secondary ms-2 heroCTAButtons">Explore Features</a>
   `;
   
   /* ===========================
      About Section
      =========================== */
   
   export const ABOUT_HTML = `
     <p><strong>Pocket Penny</strong> helps you feel confident about your finances. Whether you’re starting from scratch or importing your past transactions, everything lives in one clear, organized place.</p>
     <p><strong>Stay on top of spending:</strong> tags, search, and multi-account support give you the full picture without the spreadsheets.</p>
     <p><strong>Plan with confidence:</strong> forecast your income taxes, manage currencies, and see your money story unfold — all while knowing your data is yours to export anytime.</p>
   `;
   
   /* ===========================
      Features Section
      =========================== */
   
   export const FEATURES_HTML = `
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Clear Overview</h3>
         <p>View all your accounts and spending in one place — no flipping between banks or apps.</p>
       </div>
     </div>
   
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Insightful Trends</h3>
         <p>See where you spend most, spot awkward charges, and get early warnings so money surprises don’t ruin your day.</p>
       </div>
     </div>
   
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Flexible Start</h3>
         <p>Start fresh, or import CSVs from past tools. Either way, your data is always exportable — you stay in control.</p>
       </div>
     </div>
   
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Plan Ahead Easily</h3>
         <p>Forecast taxes, plan for upcoming bills, manage multi-currency balances — know what’s ahead.</p>
       </div>
     </div>
   
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Smart Tagging & Search</h3>
         <p>Tag your spending your way. Search quickly. Find what you need fast.</p>
       </div>
     </div>
   
     <div class="col-12 col-md-6 col-lg-4">
       <div class="feature">
         <h3 class="h5">Export & Privacy First</h3>
         <p>Your data is always yours. Export any time. We don’t connect directly to banks unless you want us to.</p>
       </div>
     </div>
   `;
   
   /* ===========================
      Why Plan Ahead Section
      =========================== */
   
   export const WHY_HTML = `
     <div class="text-center mb-4">
       <h2 class="h4 fw-bold">Why planning ahead changes everything</h2>
     </div>
     <div class="row justify-content-center">
       <div class="col-12 col-md-10 col-lg-8">
         <p><strong>Life is unpredictable.</strong> Surprise bills, variable income, unexpected costs—they happen. Planning helps soften those blows.</p>
         <p><strong>Seeing your future finances</strong> means making decisions today: saving now, avoiding late fees, and being ready for what’s coming.</p>
         <p><strong>Confidence over chaos:</strong> with clarity on your money, you’ll stop wondering “where did it all go” — and start telling your money where to go.</p>
       </div>
     </div>
   `;
   
   /* ===========================
      Demo / Signup Section
      =========================== */
   
   export const DEMO_HTML = `
     <h2 class="h4 fw-bold">Join the Public Beta</h2>
     <p>Be part of shaping Pocket Penny. Try it out free and help us build the simple, worry-free finance tool people love.</p>
     <ul class="small text-muted mb-3">
       <li>✔ Feel peace of mind about your money</li>
       <li>✔ Start fresh or bring in past data</li>
       <li>✔ Get insights, forecasts, and full control</li>
     </ul>
   `;
   
   /* ===========================
      Privacy Policy Copy
      =========================== */
   
   /** Header note under the page title */
   export const PRIVACY_BETA_NOTICE_HTML = `
     <p class="mt-3 mb-0 small text-info">
       Public beta: Pocket Penny is available at
       <a class="link-primary" href="https://pocketpenny.online" target="_blank" rel="noreferrer">
         pocketpenny.online
       </a>.
       Features may evolve and occasional interruptions may occur.
     </p>
   `;
   
   /** Sections array used for both the TOC and the section content */
   export const PRIVACY_SECTIONS = [
     {
       id: "scope",
       title: "1. Scope",
       html: `
         <p>
           This Privacy Policy explains how Pocket Penny (“we”, “us”, “our”) collects, uses, and shares information when you use our websites and applications (the “Service”). It applies to information you provide directly and information generated by your use of the Service.
         </p>
       `,
     },
     {
       id: "info-we-collect",
       title: "2. Information We Collect",
       html: `
         <ul class="mb-2">
           <li><strong>Account & authentication:</strong> name, email, profile photo/ID from your sign-in provider, authentication identifiers, session metadata.</li>
           <li><strong>App data you import:</strong> transaction files (e.g., CSV/JSON), categories/tags, accounts, income tax data. We only store what you choose to upload.</li>
           <li><strong>Usage & device:</strong> pages/features used, performance metrics, device/browser information, diagnostics and crash logs.</li>
         </ul>
         <p class="small text-muted mb-0">
           We do not connect to your financial institutions automatically. Imported data is under your control—you can delete it at any time.
         </p>
       `,
     },
     {
       id: "how-we-use",
       title: "3. How We Use Information",
       html: `
         <ul class="mb-0">
           <li>Provide and personalize core features (transaction management, tax forecasting, tagging).</li>
           <li>Process imports and generate analytics you request.</li>
           <li>Maintain security, prevent abuse, and troubleshoot.</li>
           <li>Communicate product updates and important notices.</li>
           <li>Comply with applicable laws and enforce terms.</li>
         </ul>
       `,
     },
     {
       id: "legal-bases",
       title: "4. Legal Bases (where applicable)",
       html: `
         <p class="mb-0">
           Depending on your region, we may process data under one or more legal bases: your consent; performance of a contract (providing the Service); legitimate interests (e.g., security, improving features); and compliance with legal obligations.
         </p>
       `,
     },
     {
       id: "sharing",
       title: "5. Sharing & Disclosure",
       html: `
         <ul class="mb-2">
           <li><strong>Service providers:</strong> vendors that host, store, or process data for us under contract and instructions.</li>
           <li><strong>With your direction:</strong> collaboration or export features you choose to use.</li>
           <li><strong>Legal & safety:</strong> when required by law or to protect rights, security, and integrity.</li>
           <li><strong>Business transfers:</strong> in a merger, acquisition, or asset sale, subject to continued protections.</li>
         </ul>
         <p class="small text-muted mb-0">We do not sell your personal information.</p>
       `,
     },
     {
       id: "security",
       title: "6. Security",
       html: `
         <p>
           We implement measures appropriate to the risk, including encryption in transit, access controls, backups, and monitoring. No method of transmission or storage is completely secure; we work to continuously improve our safeguards.
         </p>
       `,
     },
     {
       id: "retention",
       title: "7. Data Retention",
       html: `
         <p>
           We retain information for as long as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. You can delete your imported datasets at any time from within the app.
         </p>
       `,
     },
     {
       id: "your-rights",
       title: "8. Your Rights & Choices",
       html: `
         <ul class="mb-2">
           <li>Access, correct, or delete your personal information.</li>
           <li>Download or export your data.</li>
           <li>Object to or restrict certain processing.</li>
           <li>Opt-out of non-essential communications.</li>
         </ul>
         <p class="small text-muted mb-0">
           Some rights vary by region. Contact us if you need help exercising your rights.
         </p>
       `,
     },
     {
       id: "cookies",
       title: "9. Cookies & Similar Technologies",
       html: `
         <p>
           We use cookies/local storage for authentication, preferences, analytics, and performance. You can control cookies via your browser settings; disabling some cookies may affect functionality.
         </p>
       `,
     },
     {
       id: "third-parties",
       title: "10. Third-Party Services",
       html: `
         <p>
           The Service may link to or integrate with third-party sites or services. Their privacy practices are governed by their own policies.
         </p>
       `,
     },
     {
       id: "children",
       title: "11. Children’s Privacy",
       html: `
         <p>
           The Service is not directed to children under 13. We do not knowingly collect personal information from children. If you believe a child provided data, contact us so we can delete it.
         </p>
       `,
     },
     {
       id: "international",
       title: "12. International Transfers",
       html: `
         <p>
           Your information may be processed in countries other than your own. Where required, we implement safeguards to protect your data consistent with this policy and applicable laws.
         </p>
       `,
     },
     {
       id: "changes",
       title: "13. Changes to This Policy",
       html: `
         <p>
           We may update this policy to reflect changes to the Service or legal requirements. We will post updates here and revise the “Last updated” date above. Continued use after changes means you accept the updated policy.
         </p>
       `,
     },
     {
       id: "contact",
       title: "14. Contact Us",
       html: `
         <address class="mb-0">
           <p class="mb-1">
             <strong>Email: </strong>
             <a class="link-primary" href="mailto:privacy@pocketpenny.online">privacy@pocketpenny.online</a>
           </p>
         </address>
       `,
     },
   ];
   
   /** Right-side panel content */
   export const PRIVACY_SIDEPANEL_HTML = `
     <h3 class="h6 fw-semibold mb-3">At a glance</h3>
     <ul class="list-unstyled small mb-3">
       <li class="mb-1">• You control what you import and can delete it anytime.</li>
       <li class="mb-1">• We don’t sell your personal information.</li>
       <li class="mb-1">• Security measures help protect your data.</li>
     </ul>
     <div class="border rounded p-3">
       <div class="text-muted small mb-1">Questions?</div>
       <a class="btn btn-outline-primary btn-sm" href="mailto:privacy@pocketpenny.online">
         Contact privacy support
       </a>
     </div>
   `;
   
   /* ===========================
      Terms of Service Copy
      =========================== */
   
   /** Header note under the page title */
   export const TERMS_BETA_NOTICE_HTML = `
     <p class="mt-3 mb-0 small text-info">
       Public beta: Pocket Penny is available at
       <a class="link-primary" href="https://pocketpenny.online" target="_blank" rel="noreferrer">
         pocketpenny.online
       </a>.
       Features may change and occasional interruptions may occur.
     </p>
   `;
   
   /** Sections array used for both the TOC and the section content */
   export const TERMS_SECTIONS = [
     {
       id: "acceptance",
       title: "1. Acceptance of Terms",
       html: `
         <p>
           By accessing or using Pocket Penny (the “Service”), you agree to these Terms of Service and our Privacy Policy.
           If you do not agree, do not use the Service.
         </p>
       `,
     },
     {
       id: "beta",
       title: "2. Public Beta",
       html: `
         <p class="mb-0">
           The Service is currently offered as a private beta. Features may be added, removed, or changed without notice.
           The Service may be unavailable from time to time. Beta features may not meet the reliability or accuracy of a final release.
         </p>
       `,
     },
     {
       id: "license",
       title: "3. Use License",
       html: `
         <p class="mb-2">
           We grant you a limited, revocable, non-exclusive, non-transferable license to access and use the Service for your personal,
           non-commercial use. This license does not permit you to:
         </p>
         <ul>
           <li>Modify, copy, redistribute, or publicly display the Service or materials.</li>
           <li>Reverse engineer or attempt to extract source code.</li>
           <li>Remove proprietary notices or circumvent security.</li>
         </ul>
       `,
     },
     {
       id: "user-content",
       title: "4. User Content & Imports",
       html: `
         <p class="mb-2">
           You may upload or import data (e.g., CSV/JSON transactions, accounts, tags, income tax data). You retain any rights you have to your content.
           You grant us a limited license to store and process your content solely to provide and improve the Service.
         </p>
         <p class="small text-muted mb-0">
           You are responsible for ensuring you have the right to import data and for the accuracy of that data.
           The Service does not provide financial, legal, or tax advice.
         </p>
       `,
     },
     {
       id: "prohibited",
       title: "5. Prohibited Uses",
       html: `
         <p class="mb-2">You agree not to use the Service to:</p>
         <ul class="mb-0">
           <li>Violate laws or infringe others’ rights.</li>
           <li>Upload malicious code or disrupt the Service.</li>
           <li>Probe, scan, or bypass security controls.</li>
           <li>Access non-public areas without authorization.</li>
         </ul>
       `,
     },
     {
       id: "disclaimer",
       title: "6. Disclaimers",
       html: `
         <p class="mb-2">
           The Service is provided “as is” and “as available.” We disclaim all warranties, express or implied,
           including merchantability, fitness for a particular purpose, and non-infringement.
         </p>
         <p class="small text-muted mb-0">
           Transaction analytics and tax calculations are provided for informational purposes only and should not be relied upon as professional advice.
         </p>
       `,
     },
     {
       id: "limitations",
       title: "7. Limitation of Liability",
       html: `
         <p>
           To the maximum extent permitted by law, we will not be liable for indirect, incidental, special, consequential, or punitive damages,
           or any loss of data, revenue, or profits, arising from or related to your use of the Service.
         </p>
       `,
     },
     {
       id: "accuracy",
       title: "8. Accuracy & Availability",
       html: `
         <p>
           We strive for accuracy and uptime but make no guarantee that the Service or materials are error-free or continuously available.
           Content and features may change without notice.
         </p>
       `,
     },
     {
       id: "links",
       title: "9. Third-Party Links",
       html: `
         <p>
           The Service may include links to third-party sites or services. We are not responsible for their content or practices.
           Use of third-party services is at your own risk and subject to their terms.
         </p>
       `,
     },
     {
       id: "modifications",
       title: "10. Changes to the Service & Terms",
       html: `
         <p>
           We may update the Service and these Terms. We will post updates here and revise the “Last updated” date above.
           Your continued use constitutes acceptance of the updated Terms.
         </p>
       `,
     },
     {
       id: "termination",
       title: "11. Suspension & Termination",
       html: `
         <p>
           We may suspend or terminate access if you violate these Terms or if necessary to protect the Service or other users.
           You may stop using the Service at any time.
         </p>
       `,
     },
     {
       id: "governing-law",
       title: "12. Governing Law",
       html: `
         <p>
           These Terms are governed by the laws applicable in your place of residence or, if required, the jurisdiction where the Service is offered.
           Courts in that jurisdiction will have exclusive jurisdiction over disputes, except where applicable law provides otherwise.
         </p>
       `,
     },
     {
       id: "contact",
       title: "13. Contact",
       html: `
         <address class="mb-0">
           <p class="mb-1">
             <strong>Email: </strong>
             <a class="link-primary" href="mailto:support@pocketpenny.online">support@pocketpenny.online</a>
           </p>
         </address>
       `,
     },
   ];
   
   /** Right-side panel content */
   export const TERMS_SIDEPANEL_HTML = `
     <h3 class="h6 fw-semibold mb-3">Summary</h3>
     <ul class="list-unstyled small mb-3">
       <li class="mb-1">• Personal, non-commercial license.</li>
       <li class="mb-1">• No warranties; informational use only.</li>
       <li class="mb-1">• Liability limited to the fullest extent permitted.</li>
       <li class="mb-1">• You own your imported data.</li>
     </ul>
     <div class="border rounded p-3">
       <div class="text-muted small mb-1">Need help?</div>
       <a class="btn btn-outline-primary btn-sm" href="mailto:support@pocketpenny.online">
         Contact support
       </a>
     </div>
   `;
   